<script setup lang="ts">
import type { ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import RcSiteSelect from '@/components/rc/common/RcSiteSelect.vue'
import { getSiteDowntimeTypes } from '@/services/eos'
import { getSiteMapRacks, getSiteMapStructures } from '@/services/eos/site-map'
import { SiteType } from '@/types/core/Site'
import type { IDowntimeCreationForm } from '@/types/eos/forms'
import type SiteDowntimeOptions from '@/types/eos/SiteDowntimeOptions'
import { SiteDowntimeCategory } from '@/types/eos/SiteDowntimeOptions'
import type SiteDowntimeType from '@/types/eos/SiteDowntimeType'
import type { ISiteMapRack, ISiteMapStructure, IHostingClient } from '@/types/eos/SiteMap'
import type { InternalRuleItem, Value } from 'async-validator'
import { ElMessage, type CheckboxValueType, type FormInstance, type FormRules } from 'element-plus'
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types.mjs'
import { DateTime } from 'luxon'
import { computed, nextTick, reactive, ref, watch } from 'vue'
import EosHostingClientSelect from '../common/EosHostingClientSelect.vue'

// Enums for type safety
enum SelectionType {
  Miner = 'miner',
  Infrastructure = 'infrastructure',
}

enum MinerFilterType {
  Tag = 'tag',
  TagExclusion = 'tag_exclusion',
  Name = 'name',
  HostingClient = 'hosting_client',
}

const props = defineProps<{
  /** Site identifier object */
  site?: ISiteIdentifier
  /** Start date/time ISO 8601 string without timezone */
  startDatetime?: string
  /** End date/time ISO 8601 string without timezone */
  endDatetime?: string
  /** Timezone override, e.g. 'US/Eastern' */
  tzOverride?: string
}>()

const emit = defineEmits(['create'])

const downtimeOptions = ref<SiteDowntimeOptions[]>([])
const selectedSite = ref<ISiteIdentifier>()
const selectedDowntimeType = ref<SiteDowntimeType>()

const selectedStructures = ref<ISiteMapStructure[]>([])
const structureFilterOptions = ref<OptionType[]>([])
const structureFilterLoading = ref(false)

const selectedRacks = ref<ISiteMapRack[]>([])
const rackFilterOptions = ref<OptionType[]>([])
const rackFilterLoading = ref(false)

const selectedDateRange = ref<[string, string]>()

const downtimeMinutes = computed<string>(() => {
  let minutes: number
  if (!selectedDateRange.value) {
    minutes = 0
  } else {
    const [start, end] = selectedDateRange.value
    minutes = DateTime.fromISO(end).diff(DateTime.fromISO(start), 'minutes').minutes
  }

  return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`
})

// Conditional form field disabling
const canSelectSite = computed(() => !props.site)
const canSelectDateRange = computed(() => !!selectedSite.value && !siteHasNoStructures.value)
const canSelectStructures = computed(
  () => !!selectedSite.value && !!selectedDateRange.value && !!form.downtime_type_id && !siteHasNoStructures.value
)
const canSelectRacks = computed(() => selectedStructures.value.length > 0 && !form.whole_site)
const canSelectMinerFilters = computed(() => {
  // Miner filters can be selected if the selection type is MINER or if racks have been selected
  const minerSelection = selectedSelectionType.value === SelectionType.Miner
  const structureSelection = selectedSelectionType.value === SelectionType.Infrastructure && form.rack_ids?.length

  return minerSelection || structureSelection
})

// Do not use the below for calculation! Will cause a race condition 🏁🏎️
const timezoneForLabel = computed<string | undefined>(() =>
  props.tzOverride ? props.tzOverride : selectedSite?.value?.timezone
)
const rangeTzLabel = computed<string | undefined>(() => (timezoneForLabel.value ? `(${timezoneForLabel.value})` : ''))

const formRef = ref<FormInstance>()
const form = reactive<IDowntimeCreationForm>({
  site_id: '',
  start_datetime: undefined,
  end_datetime: undefined,
  downtime_type_id: undefined,
  selection_type: SelectionType.Infrastructure,

  // Used for infrastructure selection
  whole_site: false,
  rack_ids: undefined,

  // Used for miner selection
  miner_tags: undefined,
  miner_tags_excluded: undefined,
  miner_name_fragment: undefined,
  miner_hosting_client_ids: undefined,
})

// Add new form field for selection type
const selectedSelectionType = ref<SelectionType>(SelectionType.Infrastructure)

const selectedMinerFilterType = ref<MinerFilterType>(MinerFilterType.Tag)
const selectedHostingClients = ref<IHostingClient[]>([])

const formRules = reactive<FormRules<IDowntimeCreationForm & { miner_selection?: string }>>({
  site_id: [
    { required: true, message: 'Site is required.', trigger: ['blur', 'change'] },
    {
      validator: (rule: InternalRuleItem, value: Value, callback: (error?: string | Error) => void) => {
        // Prevent valid submission if site's timezone is not set
        // (this is now required on the BE, but it doesn't hurt to have it here)
        if (!selectedSite.value?.timezone) {
          callback('Site must have a timezone set.')
        }
        callback()
      },
    },
  ],
  start_datetime: [
    {
      required: true,
      message: 'Duration is required.',
      trigger: ['blur', 'change'],
    },
    {
      validator: (rule: InternalRuleItem, value: Value, callback: (error?: string | Error) => void) => {
        if (!form.start_datetime || !form.end_datetime) return
        if (form.end_datetime.toMillis() <= form.start_datetime.toMillis()) {
          callback('End time must be greater than start time.')
        }
        callback()
      },
    },
  ],
  end_datetime: [{ required: true, message: 'Duration is required.', trigger: ['blur', 'change'] }],
  downtime_type_id: [
    { type: 'number', required: true, message: 'Downtime type is required.', trigger: ['blur', 'change'] },
  ],
  selection_type: [{ required: true, message: 'Selection type is required.', trigger: ['blur', 'change'] }],
  rack_ids: [
    {
      validator: (rule: InternalRuleItem, value: Value, callback: (error?: string | Error) => void) => {
        if (selectedSelectionType.value === SelectionType.Infrastructure) {
          if (!Array.isArray(form.rack_ids) || form.rack_ids.length < 1) {
            callback('At least one rack is required.')
            return
          }
        }
        callback()
      },
      trigger: ['blur', 'change'],
    },
  ],
  miner_tags: [
    {
      validator: (rule: InternalRuleItem, value: Value, callback: (error?: string | Error) => void) => {
        if (
          selectedSelectionType.value === SelectionType.Miner &&
          selectedMinerFilterType.value === MinerFilterType.Tag
        ) {
          if (!Array.isArray(form.miner_tags) || form.miner_tags.length === 0) {
            callback('At least one tag is required.')
            return
          }
        }
        callback()
      },
      trigger: ['blur', 'change'],
    },
  ],
  miner_name_fragment: [
    {
      validator: (rule: InternalRuleItem, value: Value, callback: (error?: string | Error) => void) => {
        if (
          selectedSelectionType.value === SelectionType.Miner &&
          selectedMinerFilterType.value === MinerFilterType.Name
        ) {
          if (!form.miner_name_fragment || form.miner_name_fragment.trim().length === 0) {
            callback('Name fragment is required.')
            return
          }
        }
        callback()
      },
      trigger: ['blur', 'change'],
    },
  ],
  miner_hosting_client_ids: [
    {
      validator: (rule: InternalRuleItem, value: Value, callback: (error?: string | Error) => void) => {
        if (
          selectedSelectionType.value === SelectionType.Miner &&
          selectedMinerFilterType.value === MinerFilterType.HostingClient
        ) {
          if (!Array.isArray(selectedHostingClients.value) || selectedHostingClients.value.length === 0) {
            callback('At least one hosting client is required.')
            return
          }
        }
        callback()
      },
      trigger: ['blur', 'change'],
    },
  ],
})

/**
 * Generates downtime type options.
 */
async function generateGroupedDowntimeTypes() {
  const downtimeTypesData = await getSiteDowntimeTypes()

  // @ts-expect-error - Object.groupBy is not typed
  const downtimesByCategory: Partial<Record<string, SiteDowntimeType[]>> = Object.groupBy(
    downtimeTypesData,
    (dt: SiteDowntimeType) => dt.type_category
  )

  for (const downtimeCategory in downtimesByCategory) {
    downtimeOptions.value.push({
      label: downtimeCategory,
      options: downtimesByCategory[downtimeCategory]!.map((dt: SiteDowntimeType) => {
        return {
          display: `${dt.type_category} - ${dt.type_name}`,
          key: dt.id,
          value: dt,
        }
      }),
    })
  }
}

const checkAllStructures = ref(false)
const indeterminateStructures = ref(false)
const handleStructuresCheckAll = (val: CheckboxValueType) => {
  indeterminateStructures.value = false
  if (val) {
    selectedStructures.value = structureFilterOptions.value.map((_) => _.value)
  } else {
    selectedStructures.value = []

    // Clear racks
    selectedRacks.value = []
    formRef.value?.resetFields(['rack_ids'])
  }
}

watch(selectedStructures, (updatedStructures) => {
  if (updatedStructures.length === 0) {
    checkAllStructures.value = false
    indeterminateStructures.value = false
  } else if (updatedStructures.length === structureFilterOptions.value.length) {
    checkAllStructures.value = true
    indeterminateStructures.value = false
  } else {
    indeterminateStructures.value = true
  }
})

const checkAllRacks = ref(false)
const indeterminateRacks = ref(false)
const handleRacksCheckAll = (val: CheckboxValueType) => {
  indeterminateRacks.value = false
  if (val) {
    selectedRacks.value = rackFilterOptions.value.map((_) => _.value)
    form.rack_ids = selectedRacks.value.map((r) => r.id)
  } else {
    selectedRacks.value = []
    form.rack_ids = []
  }
}

watch(selectedRacks, (updatedRacks) => {
  if (updatedRacks.length === 0) {
    checkAllRacks.value = false
    indeterminateRacks.value = false
  } else if (updatedRacks.length === rackFilterOptions.value.length) {
    checkAllRacks.value = true
    indeterminateRacks.value = false
  } else {
    indeterminateRacks.value = true
  }
})

/**
 * Handles date range change. Updates form start and end datetimes.
 *
 * **Important**: the date picker operates in the browser's locale time, so we convert from
 * the locale time to the selected site's timezone when updating the form.

 * `keepLocalTime: true` prevents date math during `setZone`'s timezone conversion.
 *
 * @param {Array<string> | undefined} newRange the new date range.
 */
const handleDateRangeChange = async (newRange?: Array<string>) => {
  if (newRange) {
    const tz = props.tzOverride ? props.tzOverride : selectedSite.value?.timezone
    const [start, end] = newRange
    form.start_datetime = DateTime.fromISO(start).setZone(tz, { keepLocalTime: true })
    form.end_datetime = DateTime.fromISO(end).setZone(tz, { keepLocalTime: true })
  } else {
    form.start_datetime = undefined
    form.end_datetime = undefined

    // When date range is cleared, we need to clear the structures and racks
    // because some of them could be deactivated at that time
    form.rack_ids = []
    selectedStructures.value = []
    selectedRacks.value = []
  }
}

/**
 * Handles site selection change. Resets selected structures and racks (as these are different site-to-site).
 *
 * If a new site was selected, convert the datetime range in the el-date-picker to the site's set timezone (or prop override).
 *
 * **Note:** we setZone in `DateTime.fromISO` here as the start and end will have an offset suffix at this point.
 *
 * @param {ISiteIdentifier} val new site ID.
 */
const onSiteChange = async (val: ISiteIdentifier) => {
  form.site_id = val?.site_id

  // Clear structures and racks
  selectedStructures.value = []
  selectedRacks.value = []
  formRef.value?.resetFields(['rack_ids'])

  // Reset form validation state
  await nextTick()
  formRef.value?.clearValidate()

  const tz = props.tzOverride ? props.tzOverride : val.timezone
  form.start_datetime = form.start_datetime?.setZone(tz, { keepLocalTime: true })
  form.end_datetime = form.end_datetime?.setZone(tz, { keepLocalTime: true })

  await filterStructures('')
  if (structureFilterOptions.value?.length < 1) {
    siteHasNoStructures.value = true
  } else {
    siteHasNoStructures.value = false
  }
}

const siteHasNoStructures = ref<boolean>(false)

/**
 * Handles site structures change event: resets racks.
 */
const onStructuresChange = () => {
  selectedRacks.value = []
  form.rack_ids = []
}

/**
 * Handles racks change, updates form with rack IDs.
 *
 * @param {ISiteMapRack} val array of selected racks.
 */
const onRacksChange = (val: ISiteMapRack[]) => {
  form.rack_ids = val.map((r: ISiteMapRack) => r.id)
}

/**
 * Handles filtering of site structures.
 *
 * @param {string} query the search query.
 */
const filterStructures = async (query: string) => {
  try {
    structureFilterLoading.value = true

    const filteredResources = await getSiteMapStructures({
      page: 1,
      per_page: 100,
      order_by: 'name',
      site_id: selectedSite.value?.site_id,
      active_at: selectedDateRange.value?.[1],
      operational: selectedDowntimeType.value?.type_category == SiteDowntimeCategory.Derack ? false : true,
      name: query?.trim() || undefined,
    })

    structureFilterOptions.value = filteredResources.data.map((res: ISiteMapStructure) => {
      return {
        label: res.name,
        value: res,
      }
    })
  } catch {
    ElMessage.error('Failed to filter structures.')
    structureFilterOptions.value = []
  } finally {
    structureFilterLoading.value = false
  }
}

/**
 * Handles filtering of site structure racks.
 *
 * @param {string} query the search query.
 */
const filterRacks = async (query: string) => {
  try {
    rackFilterLoading.value = true
    const filteredResources = await getSiteMapRacks({
      page: 1,
      per_page: 500,
      order_by: 'name',
      site_id: selectedSite.value?.site_id,
      structure_ids: selectedStructures.value.map((s) => s.id).join(','),
      active_at: selectedDateRange.value?.[1],
      operational: selectedDowntimeType.value?.type_category == SiteDowntimeCategory.Derack ? false : true,
      name: query?.trim() || undefined,
    })

    rackFilterOptions.value = filteredResources.data.map((res: ISiteMapRack) => {
      return {
        label: res.name,
        value: res,
      }
    })
  } catch {
    ElMessage.error('Failed to filter racks.')
    rackFilterOptions.value = []
  } finally {
    rackFilterLoading.value = false
  }
}

/**
 * Handles downtime type change (resets structures and racks).
 *
 * @param {SiteDowntimeType} val new site downtime type value.
 */
const handleDowntimeTypeChange = (val?: SiteDowntimeType) => {
  // We will need this reference to filter structures and racks
  form.downtime_type_id = val?.id

  // When downtime type is changed, we need to clear the structures and racks
  selectedStructures.value = []
  selectedRacks.value = []
  form.rack_ids = []
}

const handleSelectionTypeChange = (val: SelectionType) => {
  form.selection_type = val

  // Clean all form values
  form.whole_site = false
  form.rack_ids = undefined
  form.miner_tags = undefined
  form.miner_name_fragment = undefined
  form.miner_hosting_client_ids = undefined

  // Clean refs
  selectedMinerFilterType.value = MinerFilterType.Tag
  selectedStructures.value = []
  selectedRacks.value = []
  selectedHostingClients.value = []
}

const handleMinerFilterTypeChange = (val: MinerFilterType) => {
  selectedMinerFilterType.value = val

  // Clean refs
  selectedHostingClients.value = []

  // Clean form
  form.miner_tags = undefined
  form.miner_name_fragment = undefined
  form.miner_hosting_client_ids = undefined
}

const handleMinerHostingClientChange = (val: IHostingClient[]) => {
  form.miner_hosting_client_ids = val.map((h) => h.id)
}

/**
 * Resets "no structures" alert closed state (allows for re-display).
 */
const onAlertClosed = () => {
  shouldShowNoStructuresAlert.value = true
}
const shouldShowNoStructuresAlert = ref<boolean>(false)

defineExpose({
  /**
   * Initializes form with:
   * - `site_id` from `site` prop.
   * - `startDatetime` and `endDatetime` from props (with conversion to site or `tzOverride` timezone).
   * - Grouped downtime types for downtime type select.
   */
  init: async () => {
    // Initialize form with site ID from props (if any)
    if (props.site) {
      selectedSite.value = props.site
      form.site_id = props.site?.site_id
    }

    // Initialize form from start and end datetime props (if any)
    if (props.startDatetime && props.endDatetime) {
      const tz = props.tzOverride ? props.tzOverride : props.site?.timezone
      selectedDateRange.value = [props.startDatetime, props.endDatetime]
      // Note: it's assumed that the props' startDatetime and endDatetime will not have an offset suffix at this point
      form.start_datetime = DateTime.fromISO(props.startDatetime).setZone(tz, { keepLocalTime: true })
      form.end_datetime = DateTime.fromISO(props.endDatetime).setZone(tz, { keepLocalTime: true })
    }

    // Fetch and group downtime types
    await generateGroupedDowntimeTypes()
  },
  submit: async () => {
    if (siteHasNoStructures.value) return
    try {
      await formRef.value?.validate()
      emit('create', form)
    } catch {
      // Validation error handled in UI
    }
  },
  resetFields: () => {
    // Reset form UI controls
    selectedSite.value = undefined
    selectedDateRange.value = undefined
    selectedDowntimeType.value = undefined
    selectedStructures.value = []
    selectedRacks.value = []
    selectedSelectionType.value = SelectionType.Infrastructure
    selectedMinerFilterType.value = MinerFilterType.Tag
    selectedHostingClients.value = []

    // Reset downtime, structure, and rack options
    downtimeOptions.value = []
    structureFilterOptions.value = []
    rackFilterOptions.value = []

    // Reset "no structures" flag
    siteHasNoStructures.value = false

    formRef.value?.resetFields()
    form.end_datetime = undefined // manually reset form.end_datetime (no prop in template)
    form.selection_type = SelectionType.Infrastructure
  },
})
</script>

<template>
  <el-form
    ref="formRef"
    :model="form"
    :rules="formRules"
    :hide-required-asterisk="true"
    label-position="left"
    label-width="168"
  >
    <el-form-item label="Site" prop="site_id">
      <RcSiteSelect
        v-model="selectedSite"
        :disabled="!canSelectSite"
        :type="SiteType.DataCenter"
        placeholder="Site"
        @change="onSiteChange"
      />
    </el-form-item>
    <el-form-item prop="start_datetime">
      <template #label>
        Range
        <span v-if="rangeTzLabel" :class="tzOverride ? 'text-xanthous-500' : 'text-text-tertiary'"
          >&nbsp;<el-tooltip
            :disabled="!tzOverride"
            type="warning"
            :content="`Site's timezone: ${selectedSite?.timezone}`"
            >{{ rangeTzLabel }}</el-tooltip
          ></span
        >
      </template>
      <el-date-picker
        v-model="selectedDateRange"
        type="datetimerange"
        range-separator="to"
        start-placeholder="Start time"
        end-placeholder="End time"
        format="YYYY-MM-DD HH:mm:ss"
        value-format="YYYY-MM-DDTHH:mm:ss"
        editable
        :disabled="!canSelectDateRange"
        @change="handleDateRangeChange"
      />
    </el-form-item>
    <el-form-item label="Downtime (per Rack)" prop="minutes">
      <el-input v-model="downtimeMinutes" disabled />
    </el-form-item>
    <el-form-item label="Type" prop="downtime_type_id">
      <el-select
        v-model="selectedDowntimeType"
        placeholder="Downtime Type"
        class="w-full"
        filterable
        value-key="id"
        clearable
        :disabled="siteHasNoStructures"
        @change="handleDowntimeTypeChange"
      >
        <el-option-group v-for="group in downtimeOptions" :key="group.label" :label="group.label">
          <el-option v-for="item in group.options" :key="item.key" :label="item.display" :value="item.value" />
        </el-option-group>
      </el-select>
    </el-form-item>

    <el-form-item label="Selection Type">
      <el-select
        v-model="selectedSelectionType"
        placeholder="Select type"
        class="w-full"
        @change="handleSelectionTypeChange"
      >
        <el-option label="By miner" :value="SelectionType.Miner" />
        <el-option label="By infrastructure" :value="SelectionType.Infrastructure" />
      </el-select>
    </el-form-item>

    <template v-if="selectedSelectionType === SelectionType.Infrastructure && selectedDowntimeType">
      <el-form-item v-if="selectedSite" prop="whole_site">
        <el-checkbox v-model="form.whole_site" label="All Infrastructure" :disabled="siteHasNoStructures" />
      </el-form-item>
      <template v-if="!form.whole_site">
        <el-form-item label="Affected Infrastructure" prop="rack_ids">
          <div class="w-full inline-flex gap-sm">
            <el-select-v2
              v-model="selectedStructures"
              :options="structureFilterOptions"
              value-key="id"
              multiple
              collapse-tags
              :max-collapse-tags="2"
              filterable
              remote
              :remote-method="filterStructures"
              :loading="structureFilterLoading"
              loading-text="Loading..."
              placeholder="Select structures..."
              :disabled="!canSelectStructures"
              clearable
              :validate-event="false"
              @change="onStructuresChange"
            >
              <template v-if="!structureFilterLoading" #header>
                <el-checkbox
                  v-model="checkAllStructures"
                  :indeterminate="indeterminateStructures"
                  :disabled="structureFilterLoading"
                  :validate-event="false"
                  @change="handleStructuresCheckAll"
                >
                  All
                </el-checkbox>
              </template>
            </el-select-v2>
            <el-select-v2
              v-model="selectedRacks"
              :options="rackFilterOptions"
              value-key="id"
              multiple
              collapse-tags
              :max-collapse-tags="2"
              filterable
              remote
              :remote-method="filterRacks"
              :loading="rackFilterLoading"
              loading-text="Loading..."
              placeholder="Select racks..."
              :disabled="!canSelectRacks"
              clearable
              @change="onRacksChange"
            >
              <template v-if="!rackFilterLoading" #header>
                <el-checkbox
                  v-model="checkAllRacks"
                  :indeterminate="indeterminateRacks"
                  :disabled="rackFilterLoading"
                  @change="handleRacksCheckAll"
                >
                  All
                </el-checkbox>
              </template>
            </el-select-v2>
          </div>
        </el-form-item>
      </template>
    </template>

    <template v-if="selectedDowntimeType && !form.whole_site">
      <el-form-item label="Miner Filter Type">
        <el-select
          v-model="selectedMinerFilterType"
          placeholder="Select filter type"
          class="w-full"
          :disabled="!canSelectMinerFilters"
          @change="handleMinerFilterTypeChange"
        >
          <el-option label="Filter by Tags" :value="MinerFilterType.Tag" />
          <el-option label="Filter by Tags (Exclusion)" :value="MinerFilterType.TagExclusion" />
          <el-option label="Filter by Name Fragment" :value="MinerFilterType.Name" />
          <el-option label="Filter by Hosting Clients" :value="MinerFilterType.HostingClient" />
        </el-select>
      </el-form-item>

      <el-form-item v-if="selectedMinerFilterType === MinerFilterType.Tag" label="Tags" prop="miner_tags">
        <el-input-tag v-model="form.miner_tags" :disabled="!canSelectMinerFilters" placeholder="Tags" class="w-full" />
      </el-form-item>

      <el-form-item
        v-if="selectedMinerFilterType === MinerFilterType.TagExclusion"
        label="Tags (Exclusion)"
        prop="miner_tags_excluded"
      >
        <el-input-tag
          v-model="form.miner_tags_excluded"
          :disabled="!canSelectMinerFilters"
          placeholder="Tags (Exclusion)"
          class="w-full"
        />
      </el-form-item>

      <el-form-item
        v-if="selectedMinerFilterType === MinerFilterType.Name"
        label="Name Fragment"
        prop="miner_name_fragment"
      >
        <el-input
          v-model="form.miner_name_fragment"
          :disabled="!canSelectMinerFilters"
          placeholder="Name fragment"
          class="w-full"
        />
      </el-form-item>

      <el-form-item
        v-if="selectedMinerFilterType === MinerFilterType.HostingClient"
        label="Hosting Clients"
        prop="miner_hosting_client_ids"
      >
        <EosHostingClientSelect
          v-model="selectedHostingClients"
          :disabled="!canSelectMinerFilters"
          multiple
          placeholder="Hosting clients"
          class="w-full"
          @change="handleMinerHostingClientChange"
        />
      </el-form-item>
    </template>

    <el-alert v-if="siteHasNoStructures" type="warning" @close="onAlertClosed"
      >Site has no structures, please select a site with structures.</el-alert
    >
  </el-form>
</template>
